/**
 * AddKinkModal Component
 * 
 * Modal dialog for adding a new kink to the database.
 * Allows setting name, category, and weirdness score.
 */

import React, { useState } from 'react';
import api from '../services/api';
import { FadeIn } from './Animations';

function AddKinkModal({ isOpen, onClose, categories, onKinkAdded }) {
    const [formData, setFormData] = useState({
        name: '',
        category: 'Uncategorized',
        weirdnessScore: 5
    });
    const [customCategory, setCustomCategory] = useState('');
    const [useCustomCategory, setUseCustomCategory] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Reset form on open
    const resetForm = () => {
        setFormData({ name: '', category: 'Uncategorized', weirdnessScore: 5 });
        setCustomCategory('');
        setUseCustomCategory(false);
        setError(null);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            setError('Name is required');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            const kinkData = {
                name: formData.name.trim(),
                category: useCustomCategory ? customCategory.trim() : formData.category,
                weirdnessScore: formData.weirdnessScore
            };

            const newKink = await api.createKink(kinkData);
            onKinkAdded(newKink);
            resetForm();
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to create kink');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <FadeIn className="glass-card w-full max-w-md p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-display text-xl font-semibold text-dark-100">
                        Add New Kink
                    </h2>
                    <button
                        onClick={() => { resetForm(); onClose(); }}
                        className="text-dark-400 hover:text-dark-200 text-2xl"
                    >
                        ×
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name */}
                    <div>
                        <label className="block text-sm text-dark-400 mb-1">Name *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g., Rope Bondage"
                            className="w-full bg-dark-900 border border-dark-600 rounded-lg px-4 py-2 text-dark-100 focus:border-wine-500 focus:outline-none"
                            autoFocus
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm text-dark-400 mb-1">Category</label>
                        <div className="flex items-center gap-2 mb-2">
                            <input
                                type="checkbox"
                                id="customCat"
                                checked={useCustomCategory}
                                onChange={(e) => setUseCustomCategory(e.target.checked)}
                                className="accent-wine-500"
                            />
                            <label htmlFor="customCat" className="text-sm text-dark-400">
                                Use custom category
                            </label>
                        </div>
                        {useCustomCategory ? (
                            <input
                                type="text"
                                value={customCategory}
                                onChange={(e) => setCustomCategory(e.target.value)}
                                placeholder="New category name"
                                className="w-full bg-dark-900 border border-dark-600 rounded-lg px-4 py-2 text-dark-100 focus:border-wine-500 focus:outline-none"
                            />
                        ) : (
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                className="w-full bg-dark-900 border border-dark-600 rounded-lg px-4 py-2 text-dark-100 focus:border-wine-500 focus:outline-none"
                            >
                                {categories.length > 0 ? (
                                    categories.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))
                                ) : (
                                    <option value="Uncategorized">Uncategorized</option>
                                )}
                            </select>
                        )}
                    </div>

                    {/* Kink Level */}
                    <div>
                        <label className="block text-sm text-dark-400 mb-1">
                            Kink Level: <span className="text-wine-400 font-mono">{formData.weirdnessScore}</span>
                        </label>
                        <input
                            type="range"
                            min="1"
                            max="10"
                            value={formData.weirdnessScore}
                            onChange={(e) => setFormData({ ...formData, weirdnessScore: parseInt(e.target.value) })}
                            className="w-full accent-wine-500"
                        />
                        <div className="flex justify-between text-xs text-dark-500">
                            <span>1 (Vanilla)</span>
                            <span>10 (Extreme)</span>
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
                            {error}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={() => { resetForm(); onClose(); }}
                            className="flex-1 px-4 py-2 bg-dark-700 text-dark-300 rounded-lg hover:bg-dark-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 btn-primary disabled:opacity-50"
                        >
                            {saving ? 'Adding...' : 'Add Kink'}
                        </button>
                    </div>
                </form>
            </FadeIn>
        </div>
    );
}

export default AddKinkModal;
